import _get from "lodash/get";
import _has from "lodash/has";
import { AnyAction, Dispatch, MiddlewareAPI } from "redux";
import { FORM_ID } from "../../../../constants";
import { RootState } from "../../../../models";
import { actionTypes as RF_AT } from "../../rapidform";
import { VALIDATION_FIELD, validateField, allValidationRules } from "../../rapidform/formValidation";
import { updateVouchers } from "../../voucher/state/actions";
import { getValidatedVouchers } from "../../voucher/state/selectors";
import { fetchCityFromPostcode } from "../api";

/**
 * Function will listen for changes to postcode input and when they match pattern of 4 characters,
 * a api call to bring will be sent trying to prefetch city from postcode. Then it will emit a new action
 * for the FORM_ID.PERSONAL_DETAILS form updating the postcode field.
 */
export const handleFormValueChanged = (
    store: MiddlewareAPI<Dispatch, RootState>,
    action: AnyAction,
    formId: string,
): Promise<unknown> | undefined => {
    // Check if it was postcode, and if so trigger update
    const { inputName, inputValue } = action.payload;
    const [prefix, validationName] = inputName.split("-");

    // Special case for POSTCODE: Try to autofill city
    if (
        (VALIDATION_FIELD.POSTCODE === validationName || VALIDATION_FIELD.PRODUCT_USER_POSTCODE === validationName) &&
        _get(inputValue, "length", 0) === 4
    ) {
        const updateCity = (cityInput: string, errorMessage?: string | null) =>
            store.dispatch({
                type: RF_AT.FORM_VALUE_CHANGED(formId),
                payload: {
                    errorMessage,
                    form: formId,
                    inputName:
                        validationName === VALIDATION_FIELD.PRODUCT_USER_POSTCODE
                            ? `${prefix}-${VALIDATION_FIELD.PRODUCT_USER_CITY}`
                            : `${prefix}-${VALIDATION_FIELD.CITY}`,
                    inputValue: cityInput,
                },
            });

        return fetchCityFromPostcode(inputValue)
            .then((city) => updateCity(city))
            .catch(() => updateCity("", validateField(inputValue, allValidationRules[validationName]) as string));
    }
};

export const populateFormFieldsFromBankIdResult = (
    store: MiddlewareAPI<Dispatch, RootState>,
    action: AnyAction,
): void => {
    [
        VALIDATION_FIELD.BIRTHDATE,
        VALIDATION_FIELD.FIRST_NAME,
        VALIDATION_FIELD.LAST_NAME,
        VALIDATION_FIELD.STREET,
        VALIDATION_FIELD.POSTCODE,
        VALIDATION_FIELD.CITY,
        VALIDATION_FIELD.EMAIL,
    ].forEach((field) => {
        store.dispatch({
            type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.PERSONAL_DETAILS),
            payload: {
                errorMessage: undefined,
                form: FORM_ID.PERSONAL_DETAILS,
                inputName: `legalOwner-${field}`,
                inputValue: action.payload[field],
                visible: true,
            },
        });
    });

    [VALIDATION_FIELD.CONTACT_EMAIL, VALIDATION_FIELD.CONTACT_PHONE].map((field) => {
        store.dispatch({
            type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.CONTACT_INFO),
            payload: {
                errorMessage: undefined,
                form: FORM_ID.CONTACT_INFO,
                inputName: `contactInfo-${field}`,
                inputValue: action.payload[field],
                visible: true,
            },
        });
    });
};

//TODO: do we need this func?
export const populateFormFieldsFromVerifyCodeSuccess = (
    store: MiddlewareAPI<Dispatch, RootState>,
    action: AnyAction,
): void => {
    [
        VALIDATION_FIELD.BIRTHDATE,
        VALIDATION_FIELD.FIRST_NAME,
        VALIDATION_FIELD.LAST_NAME,
        VALIDATION_FIELD.STREET,
        VALIDATION_FIELD.POSTCODE,
        VALIDATION_FIELD.CITY,
        VALIDATION_FIELD.EMAIL,
    ].forEach((field) => {
        if (_has(action, ["payload", "result", "legalOwner", field])) {
            store.dispatch({
                type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.PERSONAL_DETAILS),
                payload: {
                    errorMessage: undefined,
                    form: FORM_ID.PERSONAL_DETAILS,
                    inputName: `legalOwner-${field}`,
                    inputValue: action.payload.result.legalOwner[field],
                    visible: true,
                },
            });
        }
    });

    store.dispatch({
        type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.CONTACT_INFO),
        payload: {
            errorMessage: undefined,
            form: FORM_ID.CONTACT_INFO,
            inputName: `contactInfo-${VALIDATION_FIELD.CONTACT_PHONE}`,
            inputValue: action.payload.result[VALIDATION_FIELD.CONTACT_PHONE],
            visible: true,
        },
    });
};

export const populateFormFieldsFromAddExistingMember = (
    store: MiddlewareAPI<Dispatch, RootState>,
    action: AnyAction,
): void => {
    [
        VALIDATION_FIELD.BIRTHDATE,
        VALIDATION_FIELD.FIRST_NAME,
        VALIDATION_FIELD.LAST_NAME,
        VALIDATION_FIELD.PRODUCT_USER_STREET,
        VALIDATION_FIELD.PRODUCT_USER_POSTCODE,
        VALIDATION_FIELD.PRODUCT_USER_CITY,
        VALIDATION_FIELD.PRODUCT_USER_EMAIL,
    ].forEach((field) => {
        store.dispatch({
            type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.ADD_MEMBER),
            payload: {
                errorMessage: undefined,
                form: FORM_ID.ADD_MEMBER,
                inputName: `member-${field}`,
                inputValue: action.payload.productUser[field],
                visible: true,
            },
        });
    });
};

export const populateFormFieldsFromBrregLookup = (
    store: MiddlewareAPI<Dispatch, RootState>,
    action: AnyAction,
): void => {
    const fieldSet = action.payload.fieldSet || "hardwareOnly";
    const isPBAddress = !!action.payload["boxNumber"];
    let fields: string[] = [
        VALIDATION_FIELD.STREET,
        VALIDATION_FIELD.STREET_CO,
        VALIDATION_FIELD.POSTCODE,
        VALIDATION_FIELD.CITY,
    ];
    if (isPBAddress) {
        fields = [
            VALIDATION_FIELD.POSTCODE,
            VALIDATION_FIELD.CITY,
            VALIDATION_FIELD.PB_NAME,
            VALIDATION_FIELD.PB_NUMBER,
        ];
    }

    fields.forEach((field) => {
        store.dispatch({
            type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.BUSINESS_DETAILS),
            payload: {
                errorMessage: undefined,
                form: FORM_ID.BUSINESS_DETAILS,
                inputName: `${fieldSet}-${field}`,
                inputValue: action.payload[field],
                visible: true,
            },
        });
    });
};

export const handleCartUpdated = (store: MiddlewareAPI<Dispatch, RootState>): void => {
    const vouchers = getValidatedVouchers(store.getState());
    if (vouchers.length > 0) {
        store.dispatch(updateVouchers());
    }
};
